import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { KbSlider } from '@theme/kb-slider';
import { KbImageSource } from '@theme/kb-image-source';
import { kbImagePreload } from '@theme/kb-image-preload';

/**
 * @typedef {object} KbMediaVariant
 * @property {string} type
 * @property {import('@theme/kb-image-source').KbImageSourceDescriptor|null} source
 * @property {string|null} poster
 * @property {Array<{ url: string, type: string }>} sources
 */

/**
 * @typedef {object} KbShadeMediaEntry
 * @property {KbMediaVariant|null} desktop
 * @property {KbMediaVariant|null} mobile
 * @property {string} alt
 * @property {string|null} caption
 * @property {string|null} viewport
 */

/**
 * @typedef {object} KbDrawerShade
 * @property {string} name
 * @property {string|null} [description]
 * @property {string|null} [proTip]
 * @property {import('@theme/kb-image-source').KbImageSourceDescriptor|null} [modelImage]
 * @property {KbShadeMediaEntry[]} [gallery]
 */

/**
 * @typedef {object} KbDrawerVariant
 * @property {number|string} id
 * @property {string} title
 */

/**
 * @typedef {object} KbDrawerSelection
 * @property {string} kind
 * @property {number|string|null} [cartridgeProductId]
 * @property {string|null} [shadeName]
 * @property {KbDrawerVariant|null} [variant]
 * @property {string} [shadeStatus]
 */

/**
 * @typedef {HTMLElement & {
 *   selection: KbDrawerSelection|null,
 *   data: { cartridges?: Array<{ id: number|string, shades?: KbDrawerShade[] }>, shades?: KbDrawerShade[] }|null
 * }} KbPdpProductHost
 */

class KbShadeDrawer extends DialogComponent {
  constructor() {
    super();
    this.selectors = {
      host: 'kb-pdp-product',
      cartridgeType: '[data-kb-cartridge-type]',
      swatchGrid: '[data-kb-swatch-grid]',
      swatch: '[data-kb-swatch]',
      shadeSelector: '[data-kb-drawer-shade-selector]',
      detailName: '[data-kb-drawer-shade-name]',
      detailStatus: '[data-kb-drawer-shade-status]',
      detailDescription: '[data-kb-drawer-shade-description]',
      proTipWrap: '[data-kb-drawer-pro-tip-wrap]',
      proTip: '[data-kb-drawer-pro-tip]',
      carousel: '[data-kb-drawer-carousel]',
      track: '[data-kb-drawer-track]',
      progress: '[data-kb-drawer-progress]',
      progressLead: '.kb-shade-drawer__progress-lead',
      videoToggleTemplate: '[data-kb-drawer-video-toggle]',
    };
    this.eventNames = {
      optionChange: 'kb:option:change',
      variantChange: 'kb:variant:change',
      drawerOpen: 'kb:shade-drawer:open',
    };
    this.classNames = {
      slide: 'kb-shade-drawer__slide',
      figure: 'kb-shade-drawer__figure',
      image: 'kb-shade-drawer__image',
      video: 'kb-shade-drawer__video',
      caption: 'kb-shade-drawer__caption',
      videoToggle: 'kb-shade-drawer__video-toggle',
      videoPaused: 'is-video-paused',
    };
    this.settings = {
      desktopMediaQuery: '(min-width: 990px)',
      source: 'shade-drawer',
      captionTextClass: 'text-body-xs',
      sliderAriaLabel: 'Shade media',
      carouselSizes: '(min-width: 990px) 640px, (min-resolution: 2.5dppx) 168px, 248px',
      carouselWidths: [248, 400, 640, 900, 1280],
      carouselLoop: true,
    };
    this.selectedClass = 'is-selected';
    /** @type {KbPdpProductHost|null} */
    this.host = null;
    /** @type {string} */
    this.kind = 'accessory';
    this.desktop = false;
    /** @type {MediaQueryList|null} */
    this.mediaQuery = null;
    /** @type {string|null} */
    this.renderedShadeKey = null;
    /** @type {KbSlider|null} */
    this.slider = null;
    /** @type {AbortController|null} */
    this.drawerAbortController = null;
    this.imageSource = new KbImageSource();
  }

  connectedCallback() {
    super.connectedCallback();

    this.drawerAbortController = new AbortController();
    this.renderedShadeKey = null;
    this.host = /** @type {KbPdpProductHost|null} */ (
      this.closest(this.selectors.host) || document.querySelector(this.selectors.host)
    );
    this.kind = this.dataset.productKind || 'accessory';
    this.mediaQuery = window.matchMedia(this.settings.desktopMediaQuery);
    this.desktop = this.mediaQuery.matches;

    const { signal } = this.drawerAbortController;
    this.addEventListener('click', this.handleClick, { signal });
    this.mediaQuery.addEventListener('change', this.handleBreakpointChange, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.handleDialogOpen, { signal });
    document.addEventListener(this.eventNames.drawerOpen, this.handleDrawerOpenRequest, { signal });

    const track = this.querySelector(this.selectors.track);
    if (track instanceof HTMLElement) {
      this.slider = new KbSlider(track, {
        loop: this.settings.carouselLoop,
        drag: true,
        keyboard: true,
        snapAlign: 'auto',
        ariaLabel: this.dataset.sliderLabel || this.settings.sliderAriaLabel,
        progressHost: /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.carousel)),
      });
    }

    if (this.host) {
      this.host.addEventListener(this.eventNames.variantChange, this.handleVariantChange, { signal });
      if (this.host.selection) this.paint(this.host.selection);
    }

    this.warmFirstShadePhotos();
  }

  warmFirstShadePhotos() {
    const data = this.host ? this.host.data : null;
    if (!data) return;
    /** @type {KbDrawerShade[]} */
    const shades = [];
    if (Array.isArray(data.shades)) shades.push(...data.shades);
    (data.cartridges || []).forEach((cartridge) => {
      if (Array.isArray(cartridge.shades)) shades.push(...cartridge.shades);
    });

    /** @type {Array<{ url: string, srcset: string|undefined, sizes: string|undefined }>} */
    const entries = [];
    shades.forEach((shade) => {
      const media = this.shadeMediaEntries(shade)[0];
      const variant = media ? this.resolveMediaVariant(media) : null;
      const source =
        variant && variant.type === 'image'
          ? this.imageSource.withSrcset(variant.source, this.settings.carouselWidths)
          : null;
      if (!source || !source.url) return;
      entries.push({ url: source.url, srcset: source.srcset, sizes: this.settings.carouselSizes });
      if (source.sizes && source.sizes !== this.settings.carouselSizes) {
        entries.push({ url: source.url, srcset: source.srcset, sizes: source.sizes });
      }
    });
    kbImagePreload.add(entries);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    if (this.drawerAbortController) this.drawerAbortController.abort();
  }

  showDialog() {
    if (this.host && this.host.selection) this.paint(this.host.selection);
    super.showDialog();
  }

  handleDialogOpen = () => {
    if (this.slider) this.slider.rebuild();
    this.warmCarouselMedia();
  };

  warmCarouselMedia() {
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (
      this.querySelectorAll(`${this.selectors.track} img`)
    );
    images.forEach((image) => {
      image.loading = 'eager';
      this.imageSource.promote(image);
    });
  }

  /** @param {Event} event */
  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const videoToggle = target.closest(`.${this.classNames.videoToggle}`);
    if (videoToggle instanceof HTMLElement) {
      this.toggleVideo(videoToggle);
      return;
    }

    const cartridgeType = /** @type {HTMLElement|null} */ (target.closest(this.selectors.cartridgeType));
    if (cartridgeType) {
      event.preventDefault();
      this.dispatchOptionChange('cartridgeType', cartridgeType.dataset.cartridgeId);
      return;
    }

    const swatch = /** @type {HTMLElement|null} */ (target.closest(this.selectors.swatch));
    if (swatch) {
      event.preventDefault();
      if (this.kind === 'pen') {
        this.dispatchOptionChange('shade', swatch.dataset.shadeName, swatch.dataset.variantId);
      } else {
        this.dispatchOptionChange('variant', swatch.dataset.variantId);
      }
    }
  };

  handleDrawerOpenRequest = () => {
    this.showDialog();
  };

  /** @param {MediaQueryListEvent} event */
  handleBreakpointChange = (event) => {
    this.desktop = event.matches;
    this.renderedShadeKey = null;
    if (this.host && this.host.selection) this.paint(this.host.selection);
  };

  /** @param {Event} event */
  handleVariantChange = (event) => {
    this.paint(/** @type {CustomEvent} */ (event).detail);
  };

  /**
   * @param {string} optionName
   * @param {string} [value]
   * @param {string} [variantId]
   */
  dispatchOptionChange(optionName, value, variantId) {
    this.dispatchEvent(
      new CustomEvent(this.eventNames.optionChange, {
        bubbles: true,
        composed: true,
        detail: { optionName, value, variantId, source: this.settings.source },
      })
    );
  }

  /** @param {KbDrawerSelection|null} selection */
  paint(selection) {
    if (!selection) return;
    const cartridgeProductId = selection.cartridgeProductId ?? null;
    const shadeName = selection.shadeName ?? null;
    this.paintSelectors(selection);
    const shade = this.findShade(cartridgeProductId, shadeName);
    this.paintDetail(shade, selection.shadeStatus ?? '');
    const shadeKey = `${String(cartridgeProductId)}::${String(shadeName)}`;
    if (shadeKey !== this.renderedShadeKey) {
      this.buildCarousel(shade);
      this.renderedShadeKey = shadeKey;
    }
  }

  /** @param {KbDrawerSelection} selection */
  paintSelectors(selection) {
    const cartridgeProductId = selection.cartridgeProductId ?? null;
    const shadeName = selection.shadeName ?? null;
    const variantId = selection.variant ? selection.variant.id : null;

    const pills = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.cartridgeType));
    pills.forEach((pill) => {
      const selected = this.matchesId(pill.dataset.cartridgeId, cartridgeProductId);
      pill.classList.toggle(this.selectedClass, selected);
      pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    // A pen's shades belong to its cartridge, so the shade picker waits until one is picked.
    const shadeSelector = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.shadeSelector));
    if (shadeSelector) shadeSelector.hidden = cartridgeProductId == null;

    const grids = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.swatchGrid));
    grids.forEach((grid) => {
      const gridCartridgeId = grid.dataset.cartridgeId;
      const isPenGrid = Boolean(gridCartridgeId);
      const visible = !isPenGrid || this.matchesId(gridCartridgeId, cartridgeProductId);
      if (isPenGrid) grid.hidden = !visible;
      const swatches = /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll(this.selectors.swatch));
      swatches.forEach((swatch) => {
        const selected =
          this.kind === 'pen'
            ? visible && swatch.dataset.shadeName === shadeName
            : this.matchesId(swatch.dataset.variantId, variantId) || swatch.dataset.shadeName === shadeName;
        swatch.classList.toggle(this.selectedClass, selected);
        if (selected) swatch.setAttribute('aria-current', 'true');
        else swatch.removeAttribute('aria-current');
      });
    });
  }

  /**
   * @param {number|string|null} cartridgeProductId
   * @param {string|null} shadeName
   * @returns {KbDrawerShade|null}
   */
  findShade(cartridgeProductId, shadeName) {
    const data = this.host ? this.host.data : null;
    if (!data || !shadeName) return null;
    /** @type {KbDrawerShade[]} */
    let shades = [];
    if (this.kind === 'pen') {
      const cartridge = (data.cartridges || []).find((entry) => this.matchesId(entry.id, cartridgeProductId));
      shades = cartridge && cartridge.shades ? cartridge.shades : [];
    } else {
      shades = data.shades || [];
    }
    return shades.find((shade) => shade.name === shadeName) || null;
  }

  /**
   * @param {KbDrawerShade|null} shade
   * @param {string} shadeStatus
   */
  paintDetail(shade, shadeStatus) {
    const name = this.querySelector(this.selectors.detailName);
    const status = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.detailStatus));
    const description = this.querySelector(this.selectors.detailDescription);
    if (status) {
      status.textContent = shadeStatus;
      status.hidden = shadeStatus === '';
    }
    const proTipWrap = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.proTipWrap));
    const proTip = this.querySelector(this.selectors.proTip);
    if (name) name.textContent = shade ? shade.name || '' : '';
    if (description) description.textContent = shade && shade.description ? shade.description : '';
    const proTipText = shade && shade.proTip ? shade.proTip : '';
    if (proTip) proTip.textContent = proTipText;
    if (proTipWrap) proTipWrap.hidden = proTipText === '';
  }

  /**
   * @param {KbShadeMediaEntry|null} entry
   * @returns {KbMediaVariant|null}
   */
  resolveMediaVariant(entry) {
    if (!entry) return null;
    const preferred = this.desktop ? entry.desktop : entry.mobile;
    const fallback = this.desktop ? entry.mobile : entry.desktop;
    if (this.variantHasMedia(preferred)) return preferred;
    if (this.variantHasMedia(fallback)) return fallback;
    return null;
  }

  /**
   * @param {KbMediaVariant|null|undefined} variant
   * @returns {boolean}
   */
  variantHasMedia(variant) {
    if (!variant) return false;
    if (variant.type === 'video') return (variant.sources && variant.sources.length > 0) || Boolean(variant.poster);
    return Boolean(variant.source && variant.source.url);
  }

  /**
   * @param {KbDrawerShade|null} shade
   * @returns {KbShadeMediaEntry[]}
   */
  shadeMediaEntries(shade) {
    if (!shade) return [];
    const viewport = this.desktop ? 'desktop' : 'mobile';
    const gallery = (Array.isArray(shade.gallery) ? shade.gallery : []).filter(
      (entry) => !entry.viewport || entry.viewport === viewport
    );
    const hasMedia = gallery.some((entry) => this.variantHasMedia(this.resolveMediaVariant(entry)));
    if (hasMedia) return gallery;
    if (shade.modelImage) {
      return [
        {
          desktop: { type: 'image', source: shade.modelImage, poster: null, sources: [] },
          mobile: null,
          alt: shade.name || '',
          caption: null,
          viewport: null,
        },
      ];
    }
    return [];
  }

  /** @param {KbDrawerShade|null} shade */
  buildCarousel(shade) {
    const carousel = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.carousel));
    const track = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.track));
    if (!carousel || !track) return;

    track.textContent = '';
    /** @type {HTMLElement[]} */
    const slides = [];
    this.shadeMediaEntries(shade).forEach((entry) => {
      const variant = this.resolveMediaVariant(entry);
      if (!variant) return;
      const slide = this.buildSlide(variant, entry, shade);
      track.appendChild(slide);
      slides.push(slide);
    });

    if (this.slider) this.slider.setSlides(slides);
    carousel.hidden = slides.length === 0;
    const progress = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.progress));
    if (progress) progress.hidden = slides.length <= 1;
    if (this.refs.dialog.open) this.warmCarouselMedia();
  }

  /**
   * @param {KbMediaVariant} variant
   * @param {KbShadeMediaEntry} entry
   * @param {KbDrawerShade|null} shade
   * @returns {HTMLElement}
   */
  buildSlide(variant, entry, shade) {
    const slide = document.createElement('div');
    slide.className = this.classNames.slide;

    const figure = document.createElement('div');
    figure.className = this.classNames.figure;

    if (variant.type === 'video') {
      figure.appendChild(this.buildVideo(variant));
      const toggle = this.cloneVideoToggle();
      if (toggle) figure.appendChild(toggle);
      slide.classList.add(this.classNames.videoPaused);
    } else {
      figure.appendChild(this.buildImage(variant, entry, shade));
    }

    slide.appendChild(figure);

    const caption = document.createElement('p');
    caption.className = `${this.classNames.caption} ${this.settings.captionTextClass}`;
    caption.textContent = entry.caption || '';
    slide.appendChild(caption);

    return slide;
  }

  /**
   * @param {KbMediaVariant} variant
   * @param {KbShadeMediaEntry} entry
   * @param {KbDrawerShade|null} shade
   * @returns {HTMLImageElement}
   */
  buildImage(variant, entry, shade) {
    const image = document.createElement('img');
    image.className = this.classNames.image;
    image.alt = entry.alt || (shade ? shade.name : '') || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    this.imageSource.defer(
      image,
      this.imageSource.withSrcset(variant.source, this.settings.carouselWidths),
      this.settings.carouselSizes
    );
    return image;
  }

  /**
   * @param {KbMediaVariant} variant
   * @returns {HTMLVideoElement}
   */
  buildVideo(variant) {
    const video = document.createElement('video');
    video.className = this.classNames.video;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'metadata';
    if (variant.poster) video.poster = variant.poster;
    (variant.sources || []).forEach((source) => {
      const sourceElement = document.createElement('source');
      sourceElement.src = source.url;
      if (source.type) sourceElement.type = source.type;
      video.appendChild(sourceElement);
    });
    return video;
  }

  /** @returns {HTMLElement|null} */
  cloneVideoToggle() {
    const template = this.querySelector(this.selectors.videoToggleTemplate);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const node = template.content.firstElementChild;
    if (!node) return null;
    const clone = node.cloneNode(true);
    return clone instanceof HTMLElement ? clone : null;
  }

  /** @param {HTMLElement} toggle */
  toggleVideo(toggle) {
    const slide = toggle.closest(`.${this.classNames.slide}`);
    if (!(slide instanceof HTMLElement)) return;
    const video = slide.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.paused) {
      video.play().catch(() => {});
      slide.classList.remove(this.classNames.videoPaused);
    } else {
      video.pause();
      slide.classList.add(this.classNames.videoPaused);
    }
  }

  /**
   * @param {number|string|null|undefined} first
   * @param {number|string|null|undefined} second
   * @returns {boolean}
   */
  matchesId(first, second) {
    return first != null && second != null && String(first) === String(second);
  }
}

if (!customElements.get('kb-shade-drawer')) {
  customElements.define('kb-shade-drawer', KbShadeDrawer);
}

export { KbShadeDrawer };
